import React, { SyntheticEvent, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import GeneralDialogComponent from "../../../../components/GeneralDialogComponent";
import { BoxRow } from "../../../../utils/styledComponents";
import { Course, Group } from "../../../../interfaces";
import GeneralAvatarComponent from "../../../../components/GeneralAvatarComponent";
import GroupService from "../../../../services/group.service";
import { CourseService } from "../../../../services/course.service";

export default function AddCourseToGroupDialog(props: Props) {
  const STRINGS = {
    COURSE: "Course",
    ADD_COURSE_TO_GROUP: "Add Course To Group",
    CANCEL: "Cancel",
    ADD: "Add",
    SUCCESS_ADD_COURSE_TO_GROUP: "Successfully Added Course to Group",
    FAILED_ADD_COURSE_TO_GROUP: "Failed to Add Course to Group",
  };

  const { handleClose, group } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState({} as Course);

  // methods
  const handleAutoCompleteChange = (
    e: SyntheticEvent<Element, Event>,
    newValue: Course | null
  ) => {
    e.preventDefault();
    if (newValue) setSelectedCourse(newValue);
  };
  const handleAddUserToClass = async () => {
    try {
      await GroupService.createGroupRelation(group.groupId, selectedCourse);
      enqueueSnackbar(STRINGS.SUCCESS_ADD_COURSE_TO_GROUP, {
        variant: "success",
      });
      handleClose();
    } catch (error: any) {
      enqueueSnackbar(STRINGS.FAILED_ADD_COURSE_TO_GROUP, { variant: "error" });
    }
  };

  useEffect(() => {
    (async () => {
      const assignedCourses = await GroupService.readAllGroupCourses(
        group.groupId
      );
      const courses = (await CourseService.readAllCourse()).filter(
        (course) =>
          assignedCourses.findIndex(
            (assignedCourses) => assignedCourses.courseId === course.courseId
          ) === -1
      );

      setCourses(courses);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GeneralDialogComponent
      open
      onClose={handleClose}
      title={STRINGS.ADD_COURSE_TO_GROUP}
      actions={
        <>
          <Button
            variant="contained"
            color="info"
            onClick={handleClose}
            sx={{ mr: "1rem" }}
          >
            {STRINGS.CANCEL}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddUserToClass}
            disabled={!selectedCourse.courseId}
          >
            {STRINGS.ADD}
          </Button>
        </>
      }
    >
      <Autocomplete
        fullWidth
        options={courses}
        getOptionLabel={(option) => option.courseTitle}
        onChange={(e, newValue) => handleAutoCompleteChange(e, newValue)}
        renderOption={(props, option) => (
          <Box component="li" key={option.courseId} {...props}>
            <BoxRow p="0.5rem" display="flex" alignItems="center">
              <GeneralAvatarComponent src={option.courseImage} />
              <Box>
                <Typography>{option.courseTitle}</Typography>
                <Typography
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineClamp: 3,
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                  }}
                  variant="caption"
                  color="textSecondary"
                >
                  {option.courseDesc}
                </Typography>
              </Box>
            </BoxRow>
          </Box>
        )}
        renderInput={(params) => (
          <TextField margin="normal" label={STRINGS.COURSE} {...params} />
        )}
      />
    </GeneralDialogComponent>
  );
}

interface Props {
  group: Group;
  handleClose: () => void;
}
